import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { auth } from '../lib/api';
import { clearMobileAuth, hasMobileAuthPayload, isNativeApp, saveMobileAuthPayload } from '../lib/mobileAuth';

const AuthContext = createContext(null);
const VALID_ROLES = new Set(['athlete', 'coach', 'admin']);
const EMPTY_FEATURES = Object.freeze({});

function normalizeUser(user) {
  if (!user) return null;
  if (!VALID_ROLES.has(user.role)) {
    console.warn('Invalid user role received from backend:', user.role);
    return null;
  }
  return user;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [features, setFeatures] = useState(EMPTY_FEATURES);
  const [loading, setLoading] = useState(true);

  const loadFeatures = useCallback(async () => {
    try {
      const r = await auth.getFeatures();
      setFeatures(r.data || EMPTY_FEATURES);
    } catch {
      // Feature endpoint failing must not block auth; default to all-off.
      setFeatures(EMPTY_FEATURES);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const response = await auth.getMe();
        if (!isNativeApp() && !(await hasMobileAuthPayload())) {
          await auth.getCsrfToken();
        }
        if (isMounted) {
          const normalised = normalizeUser(response.data);
          setUser(normalised);
          if (normalised) {
            await loadFeatures();
          }
        }
      } catch (error) {
        // 401 means not logged in - this is normal
        if (error.response?.status === 401) {
          if (isMounted) {
            setUser(null);
          }
        } else if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
          // Backend is down - keep user null but don't redirect
          console.warn('Backend unavailable');
          if (isMounted) {
            setUser(null);
          }
        } else {
          // Other errors
          console.log('Auth check failed:', error.message);
          if (isMounted) {
            setUser(null);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [loadFeatures]);

  const login = useCallback(async (credentials) => {
    const response = await auth.login(credentials);
    await saveMobileAuthPayload(response.data);
    if (!isNativeApp()) {
      await auth.getCsrfToken();
    }
    setUser(normalizeUser(response.data.user));
    await loadFeatures();
    return response.data;
  }, [loadFeatures]);

  const register = useCallback(async (data) => {
    const response = await auth.register(data);
    await saveMobileAuthPayload(response.data);
    if (!isNativeApp()) {
      await auth.getCsrfToken();
    }
    setUser(normalizeUser(response.data.user));
    await loadFeatures();
    return response.data;
  }, [loadFeatures]);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      await clearMobileAuth();
      setUser(null);
      setFeatures(EMPTY_FEATURES);
    }
  }, []);

  // Memoise the context value so consumers don't re-render when AuthProvider
  // re-renders for unrelated reasons. login/register/logout are wrapped in
  // useCallback above so their identities are stable too.
  const value = useMemo(
    () => ({ user, features, loading, login, register, logout }),
    [user, features, loading, login, register, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** Convenience hook: returns true when a feature flag is on for the
 *  current user. Default false so the UI is safe by default. */
export const useFeatureFlag = (name) => {
  const { features } = useAuth();
  return !!features?.[name];
};
